import { call, put, takeLatest, select, takeEvery, all, } from 'typed-redux-saga';
import { fetchCategoryTreeData, fetchArticlePageData, fetchCategoryPageData, } from './data';
import { requestSearch } from './search';
import * as selectors from '../selectors';
import * as PageActions from '../actions/page';
import { throwType } from '../types/utils';
import { isResponseError, } from '../types/request';
import { getSsoForced, getUser } from '../utils/globals';
import { reportError } from '../utils/bugTracking';
const getErrorStatus = (errors) => {
    if (checkErrorsForUnauth(errors)) {
        return 'NotAllowedError';
    }
    if (checkErrorsForNotFound(errors)) {
        if (getSsoForced() && !getUser().email) {
            return 'LoginRequired';
        }
        return 'NotFound';
    }
    return 'HasError';
};
const checkErrorsForUnauth = (errors) => errors.some((err) => { var _a; return ((_a = err === null || err === void 0 ? void 0 : err.extensions) === null || _a === void 0 ? void 0 : _a.code) === 'Unauthorized'; });
const checkErrorsForNotFound = (errors) => errors.some((err) => { var _a; return ((_a = err === null || err === void 0 ? void 0 : err.extensions) === null || _a === void 0 ? void 0 : _a.code) === 'NotFound'; });
export function* requestArticlePageData(articleId, path) {
    try {
        if (!articleId)
            throw new Error('Invalid article id');
        // fetch all data required for the page
        yield* all([
            call(fetchArticlePageData, articleId),
            call(fetchCategoryTreeData),
        ]);
        yield* put(PageActions.Actions.pageNavComplete({
            pageType: 'article',
            articleId,
            path,
        }));
    }
    catch (error) {
        if (!isResponseError(error)) {
            yield* call(reportError, error);
        }
        const errors = (isResponseError(error) && error.response.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType: 'article',
            articleId,
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
export function* requestCategoryPageData(categoryId, path) {
    try {
        if (!categoryId)
            throw new Error('Invalid category id');
        // fetch all data required for the page
        yield* all([
            call(fetchCategoryPageData, categoryId),
            call(fetchCategoryTreeData),
        ]);
        yield* put(PageActions.Actions.pageNavComplete({
            pageType: 'category',
            categoryId,
            path,
        }));
    }
    catch (error) {
        if (!isResponseError(error)) {
            yield* call(reportError, error);
        }
        const errors = (isResponseError(error) && error.response.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType: 'category',
            categoryId,
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
/**
 *  Used to fetch data for any pages that don't require specific data
 */
export function* requestPage(data) {
    const { path, pageType } = data;
    try {
        yield* call(fetchCategoryTreeData);
        yield* put(PageActions.Actions.pageNavComplete({
            pageType,
            path,
        }));
    }
    catch (error) {
        if (!isResponseError(error)) {
            yield* call(reportError, error);
        }
        const errors = (isResponseError(error) && error.response.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType,
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
/**
 *  Used to fetch data for any pages that don't require specific data
 */
export function* request401Page(data) {
    const { path } = data;
    yield* put(PageActions.Actions.pageNavComplete({
        pageType: 'error401',
        path,
    }));
}
export function* requestSearchPage(query, path) {
    try {
        yield* all([
            call(fetchCategoryTreeData),
            call(requestSearch, { query, page: 1 }),
        ]);
        yield* put(PageActions.Actions.pageNavComplete({
            pageType: 'search',
            path,
            searchTerm: query,
        }));
    }
    catch (error) {
        const errors = (isResponseError(error) && error.response.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType: 'search',
            searchTerm: query,
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
export function* pageNavStart(action) {
    const payload = action.payload;
    // Don't try to reload if server has given these error types.
    const isLoginRequired = yield* select(selectors.page.isLoginRequired);
    if (isLoginRequired) {
        yield* call(request401Page, {
            path: payload.path,
        });
        return;
    }
    switch (payload.pageType) {
        case 'article':
            yield* call(requestArticlePageData, payload.articleId, payload.path);
            return;
        case 'category':
            yield* call(requestCategoryPageData, payload.categoryId, payload.path);
            return;
        case 'search':
            yield* call(requestSearchPage, payload.searchTerm, payload.path);
            return;
        case 'home':
        case 'extra':
        case 'error404':
            yield* call(requestPage, {
                path: payload.path,
                pageType: payload.pageType,
            });
            return;
        case 'error401':
            yield* call(request401Page, {
                path: payload.path,
            });
            return;
        default:
            throwType(payload);
    }
}
async function ssoLogout() {
    window.location.href = `/sso/logout${window.location.search}`;
}
export function* checkUserAuth(action) {
    const isUnauth = checkErrorsForUnauth(action.payload.errors || []);
    if (!isUnauth)
        return;
    yield* call(ssoLogout);
}
export function* watchPageNav() {
    yield* takeLatest(PageActions.ActionTypes.PAGE_NAV_START, pageNavStart);
}
export function* watchPageNavErrors() {
    yield* takeEvery(PageActions.ActionTypes.PAGE_NAV_ERROR, checkUserAuth);
}
//# sourceMappingURL=page.js.map